import { useSyncExternalStore } from 'react';

/**
 * The one place that answers "how wide are we".
 *
 * The old codebase copied this into 40-odd files:
 *
 *   const [width, setWidth] = useState(window.innerWidth);
 *   useEffect(() => { setWidth(window.innerWidth); }, [window.innerWidth]);
 *
 * A dependency array is compared at render time, so that effect can only notice
 * a resize that some unrelated re-render already followed — rotating a phone
 * changed nothing. `useSyncExternalStore` subscribes to the MediaQueryList
 * itself, which is the event the browser actually fires.
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    () => window.matchMedia(query).matches,
    () => false
  );
}

// max-width, not min-width: `(max-width: 1023px)` is true exactly when Tailwind's
// `lg:` utilities are NOT applied, so the JS branch and the CSS branch can never
// disagree by a pixel.
const PHONE = '(max-width: 639px)';
const COMPACT = '(max-width: 1023px)';

export const useIsPhone = () => useMediaQuery(PHONE);
export const useIsCompact = () => useMediaQuery(COMPACT);
